<template>
  <div class="product">
    <div class="product__left">
      <img class="product__image" :src="product.product.images.edges[0].node.image" :alt="product.product.name">
      <div class="product__block">
        <p class="product__block-name">{{ product.product.name }}</p>
        <p class="product__block-text">{{ t('productDetails.price') }} <span>{{ product.product.price }}€</span></p>
        <p class="product__block-text">
          {{ t('orders.status') }}
          <svg
            v-if="!product.attributes.code"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="#FF5959"
            class="notification__icon"
            viewBox="0 0 16 16"
          >
            <path
              d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4m.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="#37d12c"
            class="notification__icon"
            viewBox="0 0 16 16"
          >
            <path
              d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
            />
          </svg>
        </p>
        <div class="product__block-inner" v-if="product.attributes.code">
        <span class="product__block-text">{{ t('orders.code') }}</span>
        <div class="product__block-code">
          <span class="product__block-code-blur" v-if="!isCodeVisible" />
          <span class="orders__order-value" v-text="product.attributes.code" />
        </div>
        <button class="product__block-button" @click="setCodeVisible">
          <svg
              v-if="!isCodeVisible"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" />
              <path
                d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                d="m10.79 12.912-1.614-1.615a3.5 3.5 0 0 1-4.474-4.474l-2.06-2.06C.938 6.278 0 8 0 8s3 5.5 8 5.5a7 7 0 0 0 2.79-.588M5.21 3.088A7 7 0 0 1 8 2.5c5 0 8 5.5 8 5.5s-.939 1.721-2.641 3.238l-2.062-2.062a3.5 3.5 0 0 0-4.474-4.474z"
              />
              <path
                d="M5.525 7.646a2.5 2.5 0 0 0 2.829 2.829zm4.95.708-2.829-2.83a2.5 2.5 0 0 1 2.829 2.829zm3.171 6-12-12 .708-.708 12 12z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {ref} from "vue";

const props = defineProps({
  product: Object
})

const {t} = useI18n()

const isCodeVisible = ref(false)

const setCodeVisible = () => {
  isCodeVisible.value = !isCodeVisible.value
}
</script>

<style lang="scss" scoped>
.product {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 15px;
  border-bottom: 1px solid #393939;

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  &__left {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  &__image {
    width: 140px;
    border-radius: 15px;
  }

  &__block {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &-name {
      max-width: 175px;
      word-break: break-all;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      overflow: hidden;
      font-family: Poppins, sans-serif;
      font-size: 26px;
      font-weight: 500;
      color: $black;
    }

    &-text {
      display: flex;
      align-items: center;
      gap: 10px;
      font-family: Nunito, sans-serif;
      font-size: 20px;
      font-weight: 600;
      color: $black;

      & span {
        font-weight: 400;
        font-size: 18px;
      }
    }

    &-inner {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &-code {
      position: relative;

      &-blur {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: .5rem;
        background-color: #ccc;
      }
    }

    &-button {
      cursor: pointer;
      background-color: transparent;
    }
  }

  &__price {
    font-family: Poppins, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #393939;
  }

  &__text {
    font-family: Raleway, sans-serif;
    font-weight: 600;
    font-size: 22px;
    color: #393939;
  }
}

@media (max-width: 900px) {
  .product {
    &__image {
      width: 120px;
    }

    &__block {
      &-name {
        max-width: 150px;
        font-size: 20px;
      }

      &-category {
        font-size: 16px;
      }
    }

    &__text {
      font-size: 16px;
    }
  }
}

@media (max-width: 700px) {
  .product {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    &__block {
      &-name {
        max-width: unset;
      }
    }
  }
}

@media (max-width: 500px) {
  .product {
    align-items: center;

    &__block {
      align-items: center;
      gap: 5px;
    }

    &__left {
      flex-direction: column;
      align-items: center;
    }
  }
}
</style>